import type { Entity } from '../types'
import type * as PIXI from 'pixi.js'
import type { Simulation } from 'd3-force'

export class EntityRegistry {
  private static instance: EntityRegistry | null = null
  private readonly pies: Map<string, Entity> = new Map()
  private readonly nodes: Map<string, PIXI.Graphics> = new Map()
  private readonly simulations: Map<string, Simulation> = new Map()
  private readonly pieMetadata: Map<string, { centerX: number, centerY: number, radius: number }> = new Map()

  private constructor () {}

  public static getInstance (): EntityRegistry {
    if (EntityRegistry.instance === null) {
      EntityRegistry.instance = new EntityRegistry()
    }
    return EntityRegistry.instance
  }

  public registerPieSlice (pieId: string, sliceId: string, graphic: PIXI.Graphics): void {
    let pie = this.pies.get(pieId)

    if (pie === null || pie === undefined) {
      pie = new Map()
      this.pies.set(pieId, pie)
    }

    pie.set(sliceId, graphic)
  }

  public getPieSlice (pieId: string, sliceId: string): PIXI.Graphics | null {
    const pie = this.pies.get(pieId)
    return pie !== undefined ? pie.get(sliceId) : null
  }

  public getAllPieSliceGraphics (): PIXI.Graphics[] {
    return Array.from(this.pies.values()).flatMap(pie => Array.from(pie.values()))
  }

  public registerNode (nodeId: string, graphic: PIXI.Graphics): void {
    this.nodes.set(nodeId, graphic)
  }

  public hasNode (nodeId: string): boolean {
    return this.nodes.has(nodeId)
  }

  public getNode (nodeId: string): PIXI.Graphics | null {
    const node = this.nodes.get(nodeId)
    return node !== undefined ? node : null
  }

  public getAllNodes (): PIXI.Graphics[] {
    return Array.from(this.nodes.values())
  }

  public registerSimulation (pieId: string, simulation: Simulation): void {
    this.simulations.set(pieId, simulation)
  }

  public getSimulation (pieId: string): Simulation | null {
    const simulation = this.simulations.get(pieId)
    return simulation !== undefined ? simulation : null
  }

  public registerPieMetadata (pieId: string, centerX: number, centerY: number, radius: number): void {
    this.pieMetadata.set(pieId, { centerX, centerY, radius })
  }

  public getAllPieMetadata (): Array<{ centerX: number, centerY: number, radius: number }> {
    return Array.from(this.pieMetadata.values())
  }

  public getPieMetadata (pieId: string): { centerX: number, centerY: number, radius: number } | undefined {
    return this.pieMetadata.get(pieId)
  }

  public getAllGraphicsWithBounds (): Array<{ x: number, y: number, radius: number }> {
    const allGraphics: Array<{ x: number, y: number, radius: number }> = []

    // Add pie bounds
    this.pieMetadata.forEach(pie => {
      allGraphics.push({ x: pie.centerX, y: pie.centerY, radius: pie.radius })
    })

    // Add node bounds (assuming 55px radius from node-renderer.ts:112)
    this.nodes.forEach(node => {
      allGraphics.push({ x: node.position.x, y: node.position.y, radius: 55 })
    })

    return allGraphics
  }
}
